
import { useState } from "react";
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Message, ReportTemplate } from '@/types/reportTypes';
import { useAuth } from "@/hooks/useAuth";

export interface UseAIReportingChatReturn {
  messages: Message[];
  input: string;
  setInput: (value: string) => void;
  isLoading: boolean;
  handleSendMessage: () => Promise<void>;
  handleTemplateSelect: (template: ReportTemplate) => void;
  handleSaveReport: () => Promise<any>;
  resetConversation: () => void;
}

export function useAIReportingChat(): UseAIReportingChatReturn {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;
  
  // Conversation state
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! I can help you create custom reports for your business. How would you like to start? You can select a template or describe what kind of report you need.',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    if (!organizationId) {
      toast.error('Organization ID not found. Please ensure you are logged in properly.');
      return;
    }
    
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      // Filter out the welcome message for the message history
      const messageHistory = messages
        .filter((msg) => messages.indexOf(msg) > 0)
        .map((msg) => ({ role: msg.role, content: msg.content }));

      const { data, error } = await supabase.functions.invoke("generate-ai-report", {
        body: {
          prompt: input,
          organizationId,
          messageHistory
        }
      });
      
      if (error) {
        throw error;
      }
      
      if (!data.success) {
        throw new Error(data.error || "Failed to generate report");
      }

      const assistantMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: data.content || "I apologize, but I encountered an issue generating your report. Please try again.",
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Error generating report:", error);

      const errorMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content:
          "I apologize, but I encountered an error while generating your report. Please try again later.",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
      toast.error('Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (template: ReportTemplate) => {
    setInput(template.prompt);
  };

  const handleSaveReport = async () => {
    if (messages.length <= 1) {
      toast.error("There's no report to save yet");
      return;
    }
    
    if (!organizationId) {
      toast.error('Organization ID not found. Please ensure you are logged in properly.');
      return;
    }
    
    const reportName = prompt('Enter a name for this report:');
    if (!reportName) return;
    
    const lastUserMessageIndex = [...messages].reverse().findIndex(m => m.role === 'user');
    const lastUserMessage = lastUserMessageIndex !== -1 
      ? messages[messages.length - 1 - lastUserMessageIndex] 
      : null;
      
    const reportToSave = {
      name: reportName,
      description: 'Custom saved report',
      prompt: lastUserMessage?.content || '',
      category: 'Custom',
      organization_id: organizationId,
      conversation_history: JSON.stringify(messages)
    };
    
    try {
      const { data, error } = await supabase
        .from('saved_reports')
        .insert(reportToSave)
        .select()
        .single();
        
      if (error) throw error;
      toast.success('Report saved successfully!');
      return data;
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error('Failed to save report');
      throw error;
    }
  };

  const resetConversation = () => {
    if (
      confirm(
        "Are you sure you want to reset the conversation? This will clear all current messages."
      )
    ) {
      setMessages([
        {
          id: "1",
          role: "assistant",
          content:
            "Hello! I can help you create custom reports for your business. How would you like to start? You can select a template or describe what kind of report you need.",
          timestamp: new Date(),
        },
      ]);
    }
  };

  return {
    messages,
    input,
    setInput,
    isLoading,
    handleSendMessage,
    handleTemplateSelect,
    handleSaveReport,
    resetConversation,
  };
}
